import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';
import T from 'prop-types';
import React from 'react';
import * as builtinCustomUIComponents from './CustomUIComponents';
import { DynamicFormErrorBoundary } from './DynamicFormErrorBoundary';
import { DF_KEYWORD_CUSTOM_UI } from './constants';
import { DetailedError, requiredArg } from './utils';
import { localizationKeyPath } from './utils';

export const ErrorCustomUINotAvailable = DetailedError(
  'ErrorCustomUINotAvailable',
  'Custom ui is defined, but there is no custom ui available to render.'
);

export const CustomUIComponentRendered = DetailedError(
  'CustomUIComponentRendered',
  `This ui component was already rendered once at this key path.`
);

export const ErrorCustomUIComponentNotFound = DetailedError(
  'ErrorCustomUIComponentNotFound',
  'Custom ui component was not found'
);

const getCustomUIDefinition = ({
  schemaNode = requiredArg('schemaNode'),
  isPrimitiveType = requiredArg('isPrimitiveType'),
}) => {
  const definition = schemaNode[DF_KEYWORD_CUSTOM_UI];

  const { customComponent: customUIName, ...customUIProps } = isPlainObject(
    definition
  )
    ? definition
    : { customComponent: definition };

  if (!customUIName) return null;

  return {
    customUIName,
    customUIProps: isPrimitiveType
      ? {
          wrap: 'field',
          ...customUIProps,
        }
      : omit(customUIProps, ['wrap']),
  };
};

export const DynamicFormCustomUI = ({
  context = requiredArg('context'),
  schemaNode = requiredArg('schemaNode'),
  formValues = requiredArg('formValues'),
  customUIComponents = requiredArg('customUIComponents'),
  renderChildren = requiredArg('renderChildren'),
}) => {
  const isPrimitiveType = ['string', 'boolean', 'number', 'integer'].includes(
    schemaNode.type
  );

  const customUIComponentsKeyPaths = get(
    context,
    'customUIComponentsInPath',
    []
  );

  const sanitizedKeyPath = localizationKeyPath(context.keyPath).join('.');

  if (customUIComponentsKeyPaths.includes(sanitizedKeyPath)) {
    throw new CustomUIComponentRendered();
  }

  const customUIDefinition = getCustomUIDefinition({
    schemaNode,
    isPrimitiveType,
  });

  if (!customUIDefinition) {
    throw new ErrorCustomUINotAvailable();
  }

  const { customUIName, customUIProps } = customUIDefinition;

  const customUIContext = {
    ...context,
    customUIComponentsInPath: [
      ...(context.customUIComponentsInPath || []),
      sanitizedKeyPath,
    ],
  };

  const CustomUIComponent =
    customUIComponents[customUIName] || builtinCustomUIComponents[customUIName];

  if (!CustomUIComponent) {
    throw new ErrorCustomUIComponentNotFound(
      `No registered component found for "${customUIName}"`
    );
  }

  if (
    (isPrimitiveType &&
      ((context.parent === 'DynamicFormField' &&
        customUIProps.wrap === 'input') ||
        (context.parent !== 'DynamicFormField' &&
          customUIProps.wrap === 'field'))) ||
    !isPrimitiveType
  ) {
    return (
      <DynamicFormErrorBoundary
        detail={JSON.stringify(customUIContext, null, 2)}
        title={`Error rendering ${customUIName}`}
      >
        <CustomUIComponent
          {...{
            ...customUIProps,
            context: customUIContext,
            formValues,
            renderChildren,
          }}
        />
      </DynamicFormErrorBoundary>
    );
  }

  return renderChildren({
    context: customUIContext,
    formValues,
  });
};

DynamicFormCustomUI.propTypes = {
  context: T.object,
  customUIComponents: T.object,
  formValues: T.object,
  schemaNode: T.object,
  renderChildren: T.func,
};
